import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import UtilityCard from '../components/UtilityCard';
import './Dashboard.css';
import './DashboardSections.css';

const utilities = [
  {
    id: 'json-formatter',
    title: 'JSON Formatter',
    description: 'Format and validate JSON data',
    icon: '📄',
    category: 'Data & Dev Utilities',
    path: '/utility/json-formatter'
  },
  {
    id: 'hash-generator',
    title: 'Hash Generator',
    description: 'Generate MD5, SHA1, SHA256, SHA512 hashes',
    icon: '🔐',
    category: 'Data & Dev Utilities',
    path: '/utility/hash-generator'
  },
  {
    id: 'base64-encoder',
    title: 'Base64 Encoder/Decoder',
    description: 'Encode and decode Base64 strings',
    icon: '🔑',
    category: 'Data & Dev Utilities',
    path: '/utility/base64-encoder'
  },
  {
    id: 'uuid-generator',
    title: 'UUID Generator',
    description: 'Generate unique UUIDs',
    icon: '🆔',
    category: 'Data & Dev Utilities',
    path: '/utility/uuid-generator'
  },
  {
    id: 'timestamp-converter',
    title: 'Timestamp Converter',
    description: 'Convert between timestamps and dates',
    icon: '⏰',
    category: 'Data & Dev Utilities',
    path: '/utility/timestamp-converter'
  },
  {
    id: 'qr-generator',
    title: 'QR Code Generator',
    description: 'Generate QR codes for any text',
    icon: '📱',
    category: 'Everyday Tools',
    path: '/utility/qr-generator'
  },
  {
    id: 'password-generator',
    title: 'Password Generator',
    description: 'Generate secure random passwords',
    icon: '🔒',
    category: 'Everyday Tools',
    path: '/utility/password-generator'
  },
  {
    id: 'unit-converter',
    title: 'Unit Converter',
    description: 'Convert between different units',
    icon: '📏',
    category: 'Everyday Tools',
    path: '/utility/unit-converter'
  }
];

const Dashboard = () => {
  const { user } = useAuth();
  const isPremium = user?.roles?.includes('ROLE_PREMIUM');

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Welcome back, {user?.username}! 👋</h1>
        <p>Explore our powerful platform features</p>
        {!isPremium && (
          <div className="premium-banner">
            <span>⭐ Upgrade to Premium for exclusive features!</span>
          </div>
        )}
      </div>

      <section className="dashboard-section premium-section">
        <h2 className="section-header">✍️ Blog & Articles {isPremium ? '' : '(Premium)'}</h2>
        <p className="section-description">
          {isPremium 
            ? 'Create and share articles with the world. Add images, voice notes, and more!'
            : 'Upgrade to premium to create and publish articles'}
        </p>
        <div className="feature-cards">
          <Link to="/blog/my-articles" className="feature-card-link">
            <div className="feature-card">
              <div className="feature-icon">📝</div>
              <h3>My Articles</h3>
              <p>Create and manage your articles</p>
              {!isPremium && <span className="badge-premium-small">Premium</span>}
            </div>
          </Link>
          <Link to="/blog/create" className="feature-card-link">
            <div className="feature-card">
              <div className="feature-icon">➕</div>
              <h3>Create Article</h3>
              <p>Write a new article</p>
              {!isPremium && <span className="badge-premium-small">Premium</span>}
            </div>
          </Link>
        </div>
      </section>

      <section className="dashboard-section premium-section">
        <h2 className="section-header">💬 Chat & Communication {isPremium ? '' : '(Premium)'}</h2>
        <p className="section-description">
          {isPremium 
            ? 'Connect with other users, create chatrooms, and build your network'
            : 'Upgrade to premium to unlock chat features'}
        </p>
        <div className="feature-cards">
          <Link to="/chat" className="feature-card-link">
            <div className="feature-card">
              <div className="feature-icon">💬</div>
              <h3>Direct Chat</h3>
              <p>Chat one-on-one with other users</p>
              {!isPremium && <span className="badge-premium-small">Premium</span>}
            </div>
          </Link>
          <Link to="/chat/rooms" className="feature-card-link">
            <div className="feature-card">
              <div className="feature-icon">👥</div>
              <h3>Chat Rooms</h3>
              <p>Create and join chatrooms</p>
              {!isPremium && <span className="badge-premium-small">Premium</span>}
            </div>
          </Link>
          <Link to="/chat/my-profile" className="feature-card-link">
            <div className="feature-card">
              <div className="feature-icon">👤</div>
              <h3>My Profile</h3>
              <p>Manage your profile and visibility</p>
            </div>
          </Link>
        </div>
      </section>

      <section className="dashboard-section">
        <h2 className="section-header">🎮 Games</h2>
        <p className="section-description">Play exciting games and compete with other users</p>
        <div className="feature-cards">
          <Link to="/game/tic-tac-toe" className="feature-card-link">
            <div className="feature-card">
              <div className="feature-icon">⭕</div>
              <h3>Tic Tac Toe</h3>
              <p>Play Tic Tac Toe with friends or computer</p>
            </div>
          </Link>
          <Link to="/game/mosquito" className="feature-card-link">
            <div className="feature-card">
              <div className="feature-icon">🦟</div>
              <h3>Kill the Mosquito</h3>
              <p>Test your reflexes and accuracy</p>
            </div>
          </Link>
          <Link to="/game/dragon" className="feature-card-link">
            <div className="feature-card">
              <div className="feature-icon">🐉</div>
              <h3>Drag the Dragon</h3>
              <p>Guide the dragon through obstacles and collect diamonds</p>
            </div>
          </Link>
        </div>
      </section>

      <section className="dashboard-section">
        <h2 className="section-header">🎟️ Movie Booking</h2>
        <p className="section-description">Browse movies, book seats, and manage shows</p>
        <div className="feature-cards">
          <Link to="/movies" className="feature-card-link">
            <div className="feature-card">
              <div className="feature-icon">🎬</div>
              <h3>Movie Booking App</h3>
              <p>Book tickets or manage theatres</p>
            </div>
          </Link>
        </div>
      </section>

      <section className="dashboard-section">
        <h2 className="section-header">🔧 Utilities (Free)</h2>
        <p className="section-description">Free tools available to all users</p>
        <div className="utilities-grid">
          {utilities.map(utility => (
            <UtilityCard key={utility.id} utility={utility} />
          ))}
        </div>
      </section>

      <section className="dashboard-section coming-soon-section">
        <h2 className="section-header">🚀 Coming Soon</h2>
        <div className="feature-cards">
          <div className="feature-card coming-soon">
            <div className="feature-icon">🛒</div>
            <h3>E-Commerce</h3>
            <p>Buy and sell products</p>
            <span className="badge-coming-soon">Coming Soon</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;

